import type { DiagnosticCode } from '../../lint/codes';
import type { TextEdit } from '../../text/textEdit';
import { scanStructure, type UnclosedGroup } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Close containers the document leaves open — the truncated-paste case.
 *
 * The closers come off a **stack**, innermost first, which is the whole difference between
 * this rule and the legacy balancer. That one kept a depth *counter* and appended every `}`
 * before every `]`, so `{"a":[1` became `{"a":[1}]` and `[[1,2],[3,4` became `[1,2],[3,4]`
 * — unparseable in both cases (recon §3.5, B27/B28). A counter cannot express nesting; a
 * stack cannot get it wrong.
 *
 * One proposal per insertion point, carrying all of that point's closers in order, rather
 * than one proposal per bracket: the legacy fix list reported twelve identical
 * `closing brace }` entries for one truncated document, which told the user nothing.
 *
 * A mismatched closer is an insertion point too: in `{"a":[1}` the `}` ends the object, so
 * the `]` belongs immediately before it, not at the end of the document.
 */
export const closeOpenContainersRule: RepairRule = {
  id: 'close-open-containers',
  confidence: 'safe',
  priority: 50,
  resolves: [
    'syntax/close-brace-expected',
    'syntax/close-bracket-expected',
    'syntax/unexpected-eof',
  ],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    return scanStructure(ctx.source, ctx.tokens).unclosed.map((group) => {
      const edits: TextEdit[] = group.frames.map((frame) => ({
        from: group.at,
        to: group.at,
        insert: frame.kind === 'object' ? '}' : ']',
      }));

      const outermost = group.frames[group.frames.length - 1];

      return {
        ruleId: 'close-open-containers',
        span: { from: outermost?.open.from ?? group.at, to: group.at },
        edits,
        labelCode: 'fix.close-open-containers',
        labelParams: { count: group.frames.length },
        resolves: codeFor(group),
      };
    });
  },
};

/**
 * The diagnostic this insertion point answers — the code of the INNERMOST open container.
 *
 * The parser misses that container's closer first, so the lint layer always reports its code
 * at this point, whatever is open around it. A mixed group used to claim
 * `syntax/unexpected-eof`, which the linter reports only when a VALUE is missing at the end
 * (`{"a":[1,`) — never for a plain truncation. So `{"a":[1,2` carried two red rows with no
 * Fix on either and a disabled "Fix all", over a document this rule closes perfectly: the
 * most common truncated paste there is, an array cut off inside an object.
 */
function codeFor(group: UnclosedGroup): DiagnosticCode {
  return group.frames[0]?.kind === 'array'
    ? 'syntax/close-bracket-expected'
    : 'syntax/close-brace-expected';
}
